package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * An icon-only control that explains itself.
 *
 * **Icon-only is a promise that the glyph is conventional**, and the design system's rule is that
 * such a control carries a specific accessible name and a tooltip — not one or the other. Both were
 * being written by hand at fifteen call sites, as an [LIconButton] wrapped in an [LTooltip] with the
 * label repeated in three places, and the third of those places was routinely wrong: a tooltip
 * saying "Refresh" over a button announced as "Refresh usage" is two different controls to two
 * different readers.
 *
 * So the label is given once. It becomes the `aria-label` a listener hears and the tooltip a pointer
 * reveals, and they cannot disagree.
 *
 * **The native `title` is dropped.** [LIconButton] writes one by default, because a bare icon button
 * with nothing else attached needs *some* explanation on hover. Here there already is one, and the
 * browser's own tooltip is not it: it appears after its own delay, in its own place, in the operating
 * system's typeface, on top of the styled tooltip this composable just positioned — two explanations
 * of one control, offset from each other by a few hundred milliseconds and a few pixels.
 *
 * [shortcut] is the chord this control also answers to, written the way [commandChord] writes one.
 * It belongs here rather than in the label because a keyboard reader needs it *before* deciding to
 * reach for the mouse, and a control that only reveals its shortcut in a help modal is a control
 * whose shortcut nobody knows. It is drawn as [LChord] — one `<kbd>` per key — and not folded into
 * the label's sentence: `⌘` and `R` are two things a reader presses, and the accessible name stays
 * the name of the action rather than the name of the action plus a punctuated key list.
 */
@Composable
fun LIconAction(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: LButtonVariant = LButtonVariant.GHOST,
    size: LButtonSize = LButtonSize.MD,
    enabled: Boolean = true,
    loading: Boolean = false,
    placement: PopupPlacement = PopupPlacement.Top,
    shortcut: String? = null,
    icon: @Composable () -> Unit,
) {
    val shortcutKeys = remember(shortcut) { shortcut?.let(::chordKeys).orEmpty() }
    LTooltip(
        placement = placement,
        tooltipContent = {
            Row(
                Modifier.gap(Space.XS),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(label)
                // Draws nothing at all for a control with no chord, so the row is the label and
                // the gap never opens beside it.
                LChord(shortcutKeys)
            }
        },
    ) { triggerModifier ->
        LIconButton(
            label = label,
            onClick = onClick,
            modifier = triggerModifier.then(modifier),
            variant = variant,
            size = size,
            enabled = enabled,
            loading = loading,
            nativeTitle = false,
            content = icon,
        )
    }
}
